import { useCallback } from "react";
import { PrayerTime } from "@/types/page";
import { useToast } from "@/components/ui/use-toast";

interface UsePrayerTimesHandlersProps {
  prayerTimes: PrayerTime[];
  blockTitle?: string;
  setPrayerTimes?: (prayerTimes: PrayerTime[]) => void;
  setBlockTitle?: (title: string) => void;
  updateLocalState: (newPrayerTimes?: PrayerTime[], newBlockTitle?: string, newAnimationSpeed?: number) => void;
}

export function usePrayerTimesHandlers({
  prayerTimes,
  blockTitle,
  setPrayerTimes,
  setBlockTitle,
  updateLocalState,
}: UsePrayerTimesHandlersProps) {
  const { toast } = useToast();

  const handleBlockTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const newTitle = e.target.value;
    console.log("Block title changed:", newTitle);
    
    // Also update local state directly for immediate UI feedback
    if (setBlockTitle) {
      setBlockTitle(newTitle);
    }
    
    // Then update through the main state handler for persistence
    updateLocalState(undefined, newTitle);
  }, [updateLocalState, setBlockTitle]);
  
  const handleAddPrayerTime = useCallback((): void => {
    const newPrayerTime: PrayerTime = {
      id: `pt-${Date.now()}-${Math.random().toString(36).substring(2)}`,
      title: "",
      time: "08:00"
    };
    console.log("Adding new prayer time:", newPrayerTime);
    
    // Update the prayer times state directly for immediate UI feedback
    if (setPrayerTimes) {
      setPrayerTimes([...prayerTimes, newPrayerTime]);
    }
    
    // Then update through the main state handler for persistence
    updateLocalState([...prayerTimes, newPrayerTime]);
  }, [prayerTimes, updateLocalState, setPrayerTimes]);

  const handleUpdatePrayerTime = useCallback((updatedPrayerTime: PrayerTime): void => {
    console.log("Updating prayer time:", updatedPrayerTime);
    const updatedPrayerTimes = prayerTimes.map(pt => 
      pt.id === updatedPrayerTime.id ? updatedPrayerTime : pt
    );
    
    // Update the prayer times state directly for immediate UI feedback
    if (setPrayerTimes) {
      setPrayerTimes(updatedPrayerTimes);
    }
    
    updateLocalState(updatedPrayerTimes);
  }, [prayerTimes, updateLocalState, setPrayerTimes]);

  const handleRemovePrayerTime = useCallback((id: string): void => {
    console.log(`Removing prayer time: ${id}`);
    console.log(`Removing prayer time with ID: ${id}`);
    const updatedPrayerTimes = prayerTimes.filter(pt => pt.id !== id);
    console.log(`Prayer times before: ${prayerTimes.length}, after: ${updatedPrayerTimes.length}`);
    
    // Update the prayer times state directly for immediate UI feedback
    if (setPrayerTimes) {
      setPrayerTimes(updatedPrayerTimes);
    }
    
    // Force an immediate update
    updateLocalState(updatedPrayerTimes);
    
    // Show a toast for immediate feedback
    toast({
      title: "זמן תפילה הוסר",
      description: "זמן התפילה הוסר בהצלחה.",
    });
  }, [prayerTimes, updateLocalState, setPrayerTimes, toast]);

  const handleAnimationSpeedChange = useCallback((value: number[]): void => {
    console.log("Animation speed changed:", value[0]);
    updateLocalState(undefined, undefined, value[0]);
  }, [updateLocalState]);

  const handleReorderPrayerTimes = useCallback((newOrderedItems: PrayerTime[]): void => {
    console.log("Reordering prayer times:", newOrderedItems);
    
    // Update the prayer times state directly for immediate UI feedback
    if (setPrayerTimes) {
      setPrayerTimes(newOrderedItems);
    }
    
    updateLocalState(newOrderedItems);
  }, [updateLocalState, setPrayerTimes]);

  return {
    handleBlockTitleChange,
    handleAddPrayerTime,
    handleUpdatePrayerTime,
    handleRemovePrayerTime,
    handleAnimationSpeedChange,
    handleReorderPrayerTimes,
  };
}
